import { Box, Flex, FormLabel, Switch } from "@chakra-ui/react";
import { useFormikContext } from "formik";
import { SelectControl } from "formik-chakra-ui";
import React, { useMemo } from "react";
import useCheckUserRole from "src/hooks/useCheckUserRole";
import { isNonEmptyString, isNotNull } from "src/services/predicates";
import * as AF from "src/types/formTemplate";
import { z } from "zod";
import * as QuestionType from "./QuestionType";

export const FormikSchema = z
  .object({
    permissionLevel: z.discriminatedUnion("switch", [
      z.object({
        switch: z.literal("enabled"),
        level: z.string().min(1, "This field is required"),
      }),
      z.object({ switch: z.literal("disabled"), level: z.string() }),
    ]),
  })
  .and(QuestionType.FormikSchema);

export type FormikType = z.infer<typeof FormikSchema>;

export const EmptyFormikValue: Omit<FormikType, "type"> = {
  permissionLevel: { switch: "disabled", level: "" },
};

export function toFormValue(question: AF.V2.Question): FormikType {
  if (!isNotNull(question.permissionLevel)) {
    return {
      ...EmptyFormikValue,
      ...QuestionType.toFormValue(question),
    };
  }

  const isValidPermission = isNotNull(
    PermissionLevels.find((level) => level.value === question.permissionLevel)
  );
  return {
    ...QuestionType.toFormValue(question),
    permissionLevel: {
      switch: "enabled",
      level:
        isValidPermission && isNonEmptyString(question.permissionLevel)
          ? question.permissionLevel
          : "",
    },
  };
}

export function fromFormValue(
  formik: FormikType
): Pick<AF.V2.Question, "permissionLevel"> {
  if (formik.permissionLevel.switch === "disabled") {
    return { permissionLevel: undefined };
  }

  return {
    permissionLevel: formik.permissionLevel.level as AF.V2.PermissionLevel,
  };
}

const PermissionLevels = [
  { label: "District Admin", value: "district-admin" },
  { label: "Org Admin", value: "org-admin" },
  { label: "School Admin", value: "school-admin" },
];
type Props = { questionId: uuid };
export const PermissionLevel: React.FC<Props> = ({ questionId }) => {
  const formik = useFormikContext<FormikType>();

  const { isAvelaAdmin, isOrgAdmin, isDistrictAdmin, isSchoolAdmin } =
    useCheckUserRole();
  const permissions = useMemo(() => {
    if (isAvelaAdmin) {
      return PermissionLevels;
    }
    if (isOrgAdmin) {
      return PermissionLevels.filter((level) => level.value !== "admin");
    }
    if (isDistrictAdmin) {
      return PermissionLevels.filter((level) =>
        ["district-admin", "school-admin"].includes(level.value)
      );
    }
    if (isSchoolAdmin) {
      return PermissionLevels.filter((level) => level.value === "school-admin");
    }
    return [];
  }, [isAvelaAdmin, isDistrictAdmin, isOrgAdmin, isSchoolAdmin]);

  if (formik.values.permissionLevel.switch === "disabled") {
    return null;
  }

  return (
    <Flex direction="column" gap="3">
      <SelectControl
        label="Permission level"
        selectProps={{
          placeholder: "Select a permission level",
        }}
        name="permissionLevel.level"
      >
        {permissions.map((option) => {
          return (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          );
        })}
      </SelectControl>
    </Flex>
  );
};

const EXCLUDED_QUESTION_TYPE_FOR_PERMISSION_LEVEL: AF.V2.QuestionType[] = [
  AF.V2.QuestionTypes.Grades,
];

type PermissionLevelSwitchProps = {
  questionId: uuid;
};
export const PermissionLevelSwitch: React.FC<PermissionLevelSwitchProps> = ({
  questionId,
}) => {
  const formik = useFormikContext<FormikType>();
  const permissionLevel = formik.values.permissionLevel;

  React.useEffect(() => {
    (async () => {
      if (permissionLevel.switch === "disabled") {
        return;
      }

      if (
        EXCLUDED_QUESTION_TYPE_FOR_PERMISSION_LEVEL.includes(formik.values.type)
      ) {
        await formik.setFieldValue("permissionLevel.switch", "disabled");
      }
    })();
  }, [formik, permissionLevel.switch]);

  const onChangeHandler = React.useCallback(async () => {
    const nextValue =
      permissionLevel.switch === "disabled" ? "enabled" : "disabled";
    await formik.setFieldValue("permissionLevel.switch", nextValue);
  }, [formik, permissionLevel.switch]);

  if (
    EXCLUDED_QUESTION_TYPE_FOR_PERMISSION_LEVEL.includes(formik.values.type)
  ) {
    return null;
  }

  const id = `permissionLevelSwitch-${questionId}`;

  return (
    <FormLabel htmlFor={id} display="flex" flexDirection="row" gap="3">
      <Switch
        id={id}
        isChecked={formik.values.permissionLevel.switch !== "disabled"}
        onChange={onChangeHandler}
      />
      <Box>Internal-Only question</Box>
    </FormLabel>
  );
};
